import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FileCheck, Calendar, Users, Clock, CheckCircle2, XCircle, Edit2, Power, Plus, X, AlertTriangle, Loader2, CheckCircle, Package, Send, History } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { assetService } from '../../../services/assetService';
import amsProjectAssetService from '../../../services/amsProjectAssetService';
import amsProjectAuditService, { AmsProjectAudit } from '../../../services/amsProjectAuditService';
import amsAuditCheckpointService from '../../../services/amsAuditCheckpointService';
import amsEquipmentService from '../../../services/amsEquipmentService';
import { getAuditApprovalById, bulkSubmitAudits, AmsAuditApproval } from '../../../services/amsAuditApprovalService';
import { useAuth } from '../../../context/AuthContext';
import { isRequester } from '../../../utils/roleUtils';
import AmsProjectAssetDrawer from './AmsProjectAssetDrawer';
import AmsProjectAssetTreeView from './AmsProjectAssetTreeView';
import MasterAssetSelectionDrawer from '../../project/components/MasterAssetSelectionDrawer';
import AmsProjectAuditForm from './AmsProjectAuditForm';
import AmsProjectAuditEditModal from './AmsProjectAuditEditModal';
import AmsAuditHistoryDrawer from './AmsAuditHistoryDrawer';
import RightDrawer from '../../../shared/components/form/RightDrawer';
import AssetForm from '../../asset/components/AssetForm';
import projectTypeService from '../../../services/projectTypeService';
import { ProjectType } from '../../../types/projectType';

interface AmsProjectAssetManagementProps {
    project: any;
    onEquipmentUpdate?: () => void;
}

interface ManageAssetOption {
    masterAssetId: string;
    name: string;
    description?: string;
    isSelected: boolean;
    projectAssetId?: string | null;
    equipmentCount: number;
}

/**
 * True when an audit approval has newly-staged items awaiting (re)submission — i.e.
 * checkpoints a requester added/edited after a prior approval, which the backend parks
 * at status 'not_required' until the requester sends them for review (see backend
 * submitForReview: it promotes 'not_required' -> 'pending'). Without surfacing this,
 * adding an asset/checkpoint to an already-approved audit silently strands the new
 * items — the submit/resubmit buttons were gated only on draft/rejected status.
 */
const auditHasStagedItems = (a?: AmsAuditApproval | null): boolean =>
    !!a && [
        ...(a.checkpointApprovals?.projectCheckpoints || []),
        ...(a.checkpointApprovals?.assetCheckpoints || []),
    ].some((cp) => cp.status === 'not_required');

/**
 * An audit is eligible for bulk send-for-approval when it can be submitted or resubmitted:
 * a fresh draft, a rejected audit, or an already-reviewed audit with newly-staged items —
 * and, on 'known'-scope projects, only when it has at least one checkpoint (mirrors the
 * per-audit gate). Returns false when there is no approval record yet.
 */
export const isAuditSelectableForBulk = (
    approval: AmsAuditApproval | undefined,
    requirement: string | undefined,
    checkpointCount: number | undefined,
    assetCount?: number,
    equipmentCount?: number
): boolean => {
    if (!approval) return false;
    // A rejected audit can always be resubmitted (matches the previous per-audit
    // "Resubmit Audit" button, which ignored checkpoint count). The bulk endpoint
    // routes it through resubmit().
    if (approval.overallStatus === 'rejected') return true;
    // First-submit / incremental re-review path (draft, or newly-staged items after a prior review).
    const hasStagedItems = auditHasStagedItems(approval);
    const canSend =
        (approval.overallStatus === 'draft' && !approval.submittedForReview) ||
        hasStagedItems;
    if (!canSend) return false;
    // On 'known'-scope projects a first submit needs at least one checkpoint AND at
    // least one asset + one equipment for this audit (assets/equipment are per-audit).
    if (requirement === 'known') {
        if (checkpointCount === 0 || checkpointCount === undefined) return false;
        if (!assetCount || !equipmentCount) return false;
    }
    return true;
};

export default function AmsProjectAssetManagement({ project, onEquipmentUpdate }: AmsProjectAssetManagementProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);

    // Available Assets (Master Assets)
    const [availableAssets, setAvailableAssets] = useState<ManageAssetOption[]>([]);

    // Checkpoint Management Drawer
    const [openProjectAsset, setOpenProjectAsset] = useState(false);
    const [selectedAssetForCheckpoints, setSelectedAssetForCheckpoints] = useState<any>(null);

    // Master Asset Selection Drawer
    const [openMasterDrawer, setOpenMasterDrawer] = useState(false);
    const [existingProjectAssets, setExistingProjectAssets] = useState<any[]>([]);
    const [equipmentCount, setEquipmentCount] = useState<number>(0);

    // This project's own Project Type id — master assets are scoped to it (e.g. Rooftop vs Ground Mounted).
    const currentProjectTypeId = useMemo(() => {
        return typeof project?.projectTypeId === 'object'
            ? project?.projectTypeId?._id || project?.projectTypeId?.id
            : project?.projectTypeId;
    }, [project]);

    // Create New Asset (shown when no master assets exist for this project type)
    const [showCreateAssetForm, setShowCreateAssetForm] = useState(false);
    const [projectTypesForCreate, setProjectTypesForCreate] = useState<ProjectType[]>([]);

    // Audit Management
    const [existingAudits, setExistingAudits] = useState<AmsProjectAudit[]>([]);
    const [loadingAudits, setLoadingAudits] = useState(false);
    const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
    const [viewingAuditId, setViewingAuditId] = useState<string | null>(null);
    const [editingAudit, setEditingAudit] = useState<AmsProjectAudit | null>(null);
    const [showAuditForm, setShowAuditForm] = useState(false);
    const [historyAuditId, setHistoryAuditId] = useState<string | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [togglingAuditId, setTogglingAuditId] = useState<string | null>(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [auditToToggle, setAuditToToggle] = useState<AmsProjectAudit | null>(null);

    // Approval Management
    const [auditApprovals, setAuditApprovals] = useState<Map<string, AmsAuditApproval>>(new Map());
    const [auditCheckpointCounts, setAuditCheckpointCounts] = useState<Map<string, number>>(new Map());
    // Bulk send-for-approval: audits the requester has checked to submit in one action.
    const [selectedForBulk, setSelectedForBulk] = useState<Set<string>>(new Set());
    const [bulkSubmitting, setBulkSubmitting] = useState(false);
    /** Audit IDs where user has updated at least one rejected checkpoint — audit card shows "Updated" instead of "Rejected". */
    const [updatedAuditIds, setUpdatedAuditIds] = useState<Set<string>>(new Set());
    const { user } = useAuth();
    const userIsRequester = isRequester('AMS');

    // Equipment list for calculating equipment counts
    const [equipmentList, setEquipmentList] = useState<any[]>([]);

    // Fetch assets + equipment for the SELECTED AUDIT (assets/equipment are audit-scoped).
    const fetchProjectAssets = useCallback(async () => {
        if (!selectedAuditId) {
            setExistingProjectAssets([]);
            setEquipmentCount(0);
            setEquipmentList([]);
            return;
        }
        try {
            const assets = await amsProjectAssetService.getAmsProjectAssetsByAudit(selectedAuditId);
            setExistingProjectAssets(assets);

            // Fetch equipment for this audit
            try {
                const equipment = await amsEquipmentService.getByAudit(selectedAuditId);
                setEquipmentCount(equipment?.length || 0);
                setEquipmentList(equipment || []);
            } catch (error) {
                console.error("Failed to fetch equipment count", error);
                setEquipmentCount(0);
                setEquipmentList([]);
            }
        } catch (error) {
            console.error("Failed to fetch audit assets", error);
        }
    }, [selectedAuditId]);

    // Fetch Checkpoint Counts for All Audits
    const fetchCheckpointCounts = useCallback(async (audits: AmsProjectAudit[]) => {
        if (!audits || audits.length === 0) return;

        const countsMap = new Map<string, number>();

        for (const audit of audits) {
            try {
                // Fetch both project and asset checkpoints
                const [projectCheckpoints, assetCheckpoints] = await Promise.all([
                    amsAuditCheckpointService.getAuditCheckpoints(audit._id, 'project'),
                    amsAuditCheckpointService.getAuditCheckpoints(audit._id, 'asset')
                ]);

                const totalCount = (projectCheckpoints?.length || 0) + (assetCheckpoints?.length || 0);
                countsMap.set(audit._id, totalCount);
            } catch (error) {
                console.error(`Failed to fetch checkpoint count for audit ${audit._id}:`, error);
                // Set count to 0 on error to be safe
                countsMap.set(audit._id, 0);
            }
        }

        setAuditCheckpointCounts(countsMap);
    }, []);

    // Refresh checkpoint count for a specific audit
    const refreshCheckpointCount = useCallback(async (auditId: string) => {
        try {
            const [projectCheckpoints, assetCheckpoints] = await Promise.all([
                amsAuditCheckpointService.getAuditCheckpoints(auditId, 'project'),
                amsAuditCheckpointService.getAuditCheckpoints(auditId, 'asset')
            ]);

            const totalCount = (projectCheckpoints?.length || 0) + (assetCheckpoints?.length || 0);
            setAuditCheckpointCounts(prev => {
                const newMap = new Map(prev);
                newMap.set(auditId, totalCount);
                return newMap;
            });
        } catch (error) {
            console.error(`Failed to refresh checkpoint count for audit ${auditId}:`, error);
        }
    }, []);

    // Fetch Existing Audits
    const fetchAudits = useCallback(async () => {
        if (!project?._id && !project?.id) return;
        setLoadingAudits(true);
        try {
            const audits = await amsProjectAuditService.getProjectAudits(project._id || project.id);
            // Ensure we're getting an array and not filtering
            const allAudits = Array.isArray(audits) ? audits : [];
            console.log('Fetched audits (total):', allAudits.length);
            console.log('Active audits:', allAudits.filter(a => a.status === 'active').length);
            console.log('Inactive audits:', allAudits.filter(a => a.status === 'inactive').length);
            console.log('All audits:', allAudits);
            setExistingAudits(allAudits);
            // Selection (default-to-first / preserve current) is handled by the
            // reconcile effect below — NOT here. Doing it in this callback read a
            // stale `selectedAuditId` (not in the useCallback deps), so every refresh
            // after an action snapped the selection back to the first audit.

            // Load approval status for each audit
            const approvalMap = new Map<string, AmsAuditApproval>();
            for (const audit of allAudits) {
                try {
                    const approval = await getAuditApprovalById(audit._id);
                    approvalMap.set(audit._id, approval);
                } catch (error) {
                    // Approval might not exist yet, that's okay
                    console.log(`No approval record for audit ${audit._id}`);
                }
            }
            setAuditApprovals(approvalMap);

            // Fetch checkpoint counts after audits are loaded
            await fetchCheckpointCounts(allAudits);
        } catch (error) {
            console.error("Failed to fetch audits", error);
            toast.error('Failed to load audits');
        } finally {
            setLoadingAudits(false);
        }
    }, [project, fetchCheckpointCounts]);

    const toggleBulkSelection = useCallback((auditId: string) => {
        setSelectedForBulk(prev => {
            const next = new Set(prev);
            if (next.has(auditId)) next.delete(auditId); else next.add(auditId);
            return next;
        });
    }, []);

    const handleBulkSend = useCallback(async () => {
        let ids = Array.from(selectedForBulk);
        // Fallback: nothing ticked, but the currently-open audit is a draft/rejected/staged one —
        // send just that audit, so the requester doesn't have to tick a box for the audit they're
        // already viewing.
        if (ids.length === 0 && selectedAuditId &&
            isAuditSelectableForBulk(auditApprovals.get(selectedAuditId), project?.requirement, auditCheckpointCounts.get(selectedAuditId), existingProjectAssets.length, equipmentCount)) {
            ids = [selectedAuditId];
        }
        if (ids.length === 0) return;
        try {
            setBulkSubmitting(true);
            const res = await bulkSubmitAudits(ids);
            if (res.submitted > 0) toast.success(`${res.submitted} audit(s) sent for approval`);
            if (res.skipped.length > 0) {
                toast(`${res.skipped.length} audit(s) skipped: ${res.skipped.map(s => s.reason).join('; ')}`);
            }
            setSelectedForBulk(new Set());
            await fetchAudits();
            if (onEquipmentUpdate) onEquipmentUpdate();
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Failed to send audits for approval');
        } finally {
            setBulkSubmitting(false);
        }
    }, [selectedForBulk, selectedAuditId, auditApprovals, project, auditCheckpointCounts, fetchAudits, onEquipmentUpdate, existingProjectAssets.length, equipmentCount]);

    // Load available assets (Active Master Assets scoped to this project's Project Type)
    const loadAvailableAssets = useCallback(async () => {
        try {
            if (!currentProjectTypeId) {
                console.warn('AmsProjectAssetManagement: project has no projectTypeId, cannot load assets');
                setAvailableAssets([]);
                return;
            }

            console.log('AmsProjectAssetManagement: Loading active assets for project type', currentProjectTypeId);

            // Fetch all active assets for this project type, with pagination handling since API limits to 100
            let allAssets: any[] = [];
            let page = 1;
            let hasMore = true;
            const LIMIT = 100;

            while (hasMore) {
                const response = await assetService.getAssets({
                    status: 'active',
                    projectTypeId: currentProjectTypeId,
                    limit: LIMIT,
                    page: page
                });

                const assets = response.assets || [];
                allAssets = [...allAssets, ...assets];

                // Check if we have more pages
                // Support different API response structures for total count
                const total = response.pagination?.total || (response as any).total || (response as any).totalDocs || 0;

                if (allAssets.length >= total || assets.length < LIMIT) {
                    hasMore = false;
                } else {
                    page++;
                }
            }

            console.log('AmsProjectAssetManagement: Total assets loaded:', allAssets.length);

            const options: ManageAssetOption[] = allAssets.map((asset: any) => ({
                masterAssetId: asset._id || asset.id,
                name: asset.name,
                description: asset.description,
                isSelected: false, // Not used in new view
                projectAssetId: null, // Not used in new view
                equipmentCount: 0
            }));

            setAvailableAssets(options);
        } catch (error) {
            console.error('Failed to load master assets:', error);
            toast.error('Failed to load available assets');
        }
    }, [currentProjectTypeId]);

    // Open the "Create New Asset" form, pre-scoped to this project's Project Type.
    const handleOpenCreateAsset = async () => {
        setShowCreateAssetForm(true);
        if (projectTypesForCreate.length === 0) {
            try {
                const types = await projectTypeService.getAllProjectTypes();
                // projectTypeService's ProjectType and types/projectType's ProjectType
                // are structurally near-identical (service omits the optional isActive);
                // cast to the locally-imported shape consumed by AssetForm.
                setProjectTypesForCreate(types as unknown as ProjectType[]);
            } catch (error) {
                console.error('Failed to load project types:', error);
                toast.error('Failed to load project types');
            }
        }
    };

    useEffect(() => {
        if (project) {
            loadAvailableAssets();
            fetchAudits(); // Fetch audits when project changes
        }
    }, [project, loadAvailableAssets, fetchAudits]);

    // Load assets/equipment whenever the selected audit changes (they are audit-scoped).
    useEffect(() => {
        fetchProjectAssets();
    }, [fetchProjectAssets]);

    // Single source of truth for audit selection. Runs off live state (selectedAuditId
    // in deps — no stale closure), so it deterministically: preserves the current
    // selection when it still exists, otherwise defaults to the first audit, and clears
    // when there are none. Preserve (after an action) → clicked audit → first.
    useEffect(() => {
        if (existingAudits.length === 0) {
            if (selectedAuditId !== null) setSelectedAuditId(null);
            return;
        }
        const stillExists = !!selectedAuditId && existingAudits.some(audit => audit._id === selectedAuditId);
        if (!stillExists) {
            setSelectedAuditId(existingAudits[0]._id);
        }
    }, [existingAudits, selectedAuditId]);

    // Refresh project assets when equipment is updated (includes asset removal)
    const handleEquipmentUpdate = useCallback(() => {
        // Call the parent's onEquipmentUpdate if provided
        if (onEquipmentUpdate) {
            onEquipmentUpdate();
        }
        // Also refresh project assets to reflect any changes (like removals)
        fetchProjectAssets();
        // And refetch audits + their approvals so the AMS rejected/updated badges
        // update after a checkpoint change (the badges read from auditApprovals).
        fetchAudits();
    }, [onEquipmentUpdate, fetchAudits, fetchProjectAssets]);

    // Handle audit creation success
    const handleAuditSuccess = useCallback(async () => {
        await fetchAudits();
        if (onEquipmentUpdate) {
            onEquipmentUpdate();
        }
        // After fetching, if no audit is selected, the first one will be selected automatically in fetchAudits
    }, [fetchAudits, onEquipmentUpdate]);

    // Handle audit update success
    const handleAuditUpdateSuccess = useCallback(() => {
        fetchAudits();
        // Propagate to the parent (/projects list + progress header) so the
        // project's status/progress reflects the audit change without a reload.
        if (onEquipmentUpdate) {
            onEquipmentUpdate();
        }
        setEditingAudit(null);
    }, [fetchAudits, onEquipmentUpdate]);

    // Handle audit status toggle
    const handleToggleAuditStatus = useCallback(async (audit: AmsProjectAudit) => {
        const newStatus = audit.status === 'active' ? 'inactive' : 'active';
        setTogglingAuditId(audit._id);

        try {
            await amsProjectAuditService.updateAudit(audit._id, {
                status: newStatus,
            } as any);

            toast.success(`Audit ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
            fetchAudits();
            // Propagate to the parent (/projects list + progress header) so the
            // project's status/progress reflects the toggle without a reload.
            if (onEquipmentUpdate) {
                onEquipmentUpdate();
            }
            setIsConfirmOpen(false);
            setAuditToToggle(null);
        } catch (error: any) {
            console.error('Failed to toggle audit status:', error);
            toast.error(error?.response?.data?.message || 'Failed to update audit status');
        } finally {
            setTogglingAuditId(null);
        }
    }, [fetchAudits, onEquipmentUpdate]);

    // Handle toggle button click
    const handleToggleClick = useCallback((e: React.MouseEvent, audit: AmsProjectAudit) => {
        e.stopPropagation();
        setAuditToToggle(audit);
        setIsConfirmOpen(true);
    }, []);

    const handleOpenCheckpoints = async (node: any) => {
        // Node comes from AmsProjectAssetTreeView (Equipment node or Asset Card)
        // Equipment has projectAssetId which references AmsProjectAsset
        // AmsProjectAsset has masterAssetId which references Master Asset

        // Allow if we have projectAssetId OR masterAssetId
        if (!node.projectAssetId && !node.masterAssetId) {
            toast.error('Cannot manage checkpoints: Asset ID missing');
            return;
        }

        // If we only have masterAssetId (New Asset scenario from available list)
        if (node.masterAssetId && !node.projectAssetId) {
            setSelectedAssetForCheckpoints({
                name: node.name || node.assetType || 'New Asset',
                masterAssetId: node.masterAssetId,
            });
            setOpenProjectAsset(true);
            return;
        }

        // Fetch the ProjectAsset to get masterAssetId
        try {
            const projectAsset = await amsProjectAssetService.getAmsProjectAssetById(node.projectAssetId);

            setSelectedAssetForCheckpoints({
                _id: node.projectAssetId,
                projectAssetId: node.projectAssetId,
                name: node.assetType,
                masterAssetId: typeof projectAsset.masterAssetId === 'string' ? projectAsset.masterAssetId : projectAsset.masterAssetId._id, // Pass masterAssetId for fetching master checkpoints
            });
            setOpenProjectAsset(true);
        } catch (error) {
            console.error('Failed to fetch AMS project asset:', error);
            toast.error('Failed to load asset information');
        }
    };

    // Format audit frequency for display
    const formatFrequency = (frequency: string) => {
        const map: Record<string, string> = {
            'yearly': 'Yearly',
            'half_yearly': 'Half Yearly',
            'quarterly': 'Quarterly',
        };
        return map[frequency] || frequency;
    };

    // audit.auditTypeId is a populated { _id, name } object from the backend, or
    // occasionally a bare id string (e.g. right after an optimistic local update).
    const getAuditTypeLabel = (audit: AmsProjectAudit) =>
        typeof audit.auditTypeId === 'string' ? audit.auditTypeId : audit.auditTypeId?.name || '';

    // Format date for display
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Format audit start date (handles YYYY-MM-DD or DD/MM/YYYY)
    const formatAuditStartDate = (dateStr: string | undefined) => {
        if (!dateStr || !dateStr.trim()) return '—';
        const s = dateStr.trim();
        const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddmmyyyy) {
            const [, d, m, y] = ddmmyyyy;
            const date = new Date(parseInt(y ?? '', 10), parseInt(m ?? '', 10) - 1, parseInt(d ?? '', 10));
            return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
        const date = new Date(s.slice(0, 10));
        return isNaN(date.getTime()) ? '—' : date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    // Shared Assets & Equipment card. Rendered inside the selected audit's connected
    // panel when audits exist (so it visually belongs to that audit), or standalone as
    // a fallback when there are no audits yet (so assets can be added first).
    const assetsAndEquipmentCard = (
        <div className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-6">
                <Package size={18} className="text-blue-600" />
                <h4 className="font-semibold text-gray-900">Assets and Equipment</h4>
            </div>
            <AmsProjectAssetTreeView
                project={project}
                onEquipmentUpdate={handleEquipmentUpdate}
                onOpenCheckpoints={handleOpenCheckpoints}
                availableAssets={availableAssets}
                onAddMasterAsset={project?.requirement === 'known' ? () => setOpenMasterDrawer(true) : undefined}
                projectAssets={existingProjectAssets}
                selectedAuditId={selectedAuditId}
                selectedAudit={existingAudits.find(a => a._id === selectedAuditId) || null}
                onCheckpointsUpdated={selectedAuditId ? () => { refreshCheckpointCount(selectedAuditId); fetchAudits(); } : undefined}
                selectedAuditApproval={selectedAuditId ? auditApprovals.get(selectedAuditId) ?? null : null}
                projectApprovalSections={project?.projectApprovalSections ?? undefined}
                onAuditCheckpointsUpdated={selectedAuditId ? (auditId: string) => setUpdatedAuditIds(prev => new Set(prev).add(auditId)) : undefined}
            />
        </div>
    );

    return (
        <div className="h-full space-y-6">
            {/* Audits Section - At Top */}
            {(project?._id || project?.id) && (
                <>
                    {/* Single submit control: sends the ticked audits, or — when none are ticked —
                        the currently-open audit if it's a draft/rejected/staged one. Handles first
                        submit and resubmit via the bulk endpoint. */}
                    {userIsRequester && existingAudits.length > 0 && (() => {
                        const openApproval = selectedAuditId ? auditApprovals.get(selectedAuditId) : undefined;
                        const openAuditSubmittable = !!selectedAuditId &&
                            isAuditSelectableForBulk(openApproval, project?.requirement, auditCheckpointCounts.get(selectedAuditId), existingProjectAssets.length, equipmentCount);
                        const canSend = selectedForBulk.size > 0 || openAuditSubmittable;
                        return (
                            <div className="flex items-center justify-end gap-3">
                                {selectedForBulk.size > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setSelectedForBulk(new Set())}
                                        className="text-sm text-slate-600 hover:text-slate-800"
                                    >
                                        Clear
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleBulkSend}
                                    disabled={bulkSubmitting || !canSend}
                                    title={!canSend ? 'Open a draft/rejected audit, or tick one or more audits, to send for approval' : undefined}
                                    className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${bulkSubmitting || !canSend
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                        }`}
                                >
                                    {bulkSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                                    {selectedForBulk.size > 0 ? `Send ${selectedForBulk.size} for approval` : 'Send for approval'}
                                </button>
                            </div>
                        );
                    })()}

                    <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm overflow-hidden">
                        <div className="p-4 sm:p-5 space-y-4">
                            {/* Section header */}
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 text-slate-600">
                                        <FileCheck size={18} strokeWidth={2} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-900 tracking-tight">Project Audits</h4>
                                        <p className="text-xs text-slate-500 mt-0.5">Select one to view assets &amp; checkpoints below</p>
                                    </div>
                                    {existingAudits.length > 0 && (
                                        <span className="hidden sm:inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 text-xs font-medium bg-slate-100 text-slate-600 rounded-full">
                                            {existingAudits.length}
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={() => setShowAuditForm(true)}
                                    className="inline-flex items-center gap-2 h-9 px-4 text-sm font-medium rounded-lg bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors"
                                    title="Create Audit"
                                >
                                    <Plus size={16} strokeWidth={2} />
                                    Create Audit
                                </button>
                            </div>

                            {/* Audit selector — connected tabs: the selected tab merges into the detail
                                panel below (like the approver's audit view) and drives the content further down. */}
                            {existingAudits.length > 0 && !loadingAudits && (() => {
                                const getInitials = (name: string) => {
                                    const parts = name.trim().split(/\s+/);
                                    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                                    return (name.slice(0, 2) || '—').toUpperCase();
                                };
                                const selAudit = existingAudits.find((a) => a._id === selectedAuditId) || null;
                                const selApproval = selAudit ? auditApprovals.get(selAudit._id) : undefined;
                                const selRejected = selApproval?.overallStatus === 'rejected';
                                const selUpdated = !!selRejected && !!selAudit && updatedAuditIds.has(selAudit._id);
                                // Panel tint follows selection state (updated/rejected/other → amber/red/blue).
                                const panelClass = selUpdated
                                    ? 'border-amber-400 bg-amber-50/40'
                                    : selRejected
                                    ? 'border-red-400 bg-red-50/40'
                                    : 'border-blue-500 bg-blue-50/40';

                                // Rejection detail for the selected audit (shown in the panel).
                                const selRejSections: string[] = [];
                                const selRejComments: string[] = [];
                                if (selApproval) {
                                    if (selApproval.sections?.auditType?.status === 'rejected') { selRejSections.push('Audit info'); if (selApproval.sections.auditType.approverComments) selRejComments.push(selApproval.sections.auditType.approverComments); }
                                    if (selApproval.sections?.auditors?.status === 'rejected') { selRejSections.push('Auditors'); if (selApproval.sections.auditors.approverComments) selRejComments.push(selApproval.sections.auditors.approverComments); }
                                    if (selApproval.sections?.assetsAndEquipment?.status === 'rejected') { selRejSections.push('Assets & equipment'); if (selApproval.sections.assetsAndEquipment.approverComments) selRejComments.push(selApproval.sections.assetsAndEquipment.approverComments); }
                                }
                                const selRejCpCount = selApproval
                                    ? (selApproval.checkpointApprovals?.projectCheckpoints?.filter((cp: { status: string }) => cp.status === 'rejected').length ?? 0) +
                                      (selApproval.checkpointApprovals?.assetCheckpoints?.filter((cp: { status: string }) => cp.status === 'rejected').length ?? 0)
                                    : 0;
                                const selAuditorNames = (selAudit?.auditors || []).map((a: { name?: string }) => a.name || '—');

                                return (
                                    <div>
                                        {/* Tab strip */}
                                        <div className="flex flex-wrap items-end gap-2">
                                            {existingAudits.map((audit) => {
                                                const isSelected = selectedAuditId === audit._id;
                                                const approval = auditApprovals.get(audit._id);
                                                const isRejected = approval?.overallStatus === 'rejected';
                                                const auditUpdated = isRejected && updatedAuditIds.has(audit._id);
                                                const approved = approval?.overallStatus === 'approved_to_start' || approval?.overallStatus === 'partially_approved';
                                                const submitted = !!approval?.submittedForReview;
                                                const auditorCount = (audit.auditors || []).length;
                                                // Selected connects downward into the panel; unselected is a light status-tinted box.
                                                const selectedConnect = auditUpdated
                                                    ? 'border-amber-400 bg-amber-50 text-amber-900'
                                                    : isRejected
                                                    ? 'border-red-400 bg-red-50 text-red-900'
                                                    : 'border-blue-500 bg-blue-50 text-blue-900';
                                                const unselectedTint = auditUpdated
                                                    ? 'border-amber-300 bg-amber-50/60 hover:border-amber-400 text-slate-800'
                                                    : isRejected
                                                    ? 'border-red-300 bg-red-50/60 hover:border-red-400 text-slate-800'
                                                    : approved
                                                    ? 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-300 text-slate-800'
                                                    : 'border-slate-200 bg-white hover:border-slate-300 text-slate-800';
                                                return (
                                                    <div
                                                        key={audit._id}
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-pressed={isSelected}
                                                        aria-label={`${getAuditTypeLabel(audit)}, ${formatFrequency(audit.auditFrequency)}. ${isSelected ? 'Selected.' : 'Click to select.'} ${auditUpdated ? 'Updated.' : isRejected ? 'Rejected.' : approved ? 'Approved.' : ''}`}
                                                        onClick={() => setSelectedAuditId(audit._id)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedAuditId(audit._id); } }}
                                                        className={`relative min-w-[190px] max-w-[280px] flex-1 border-2 px-3.5 py-2.5 text-left cursor-pointer transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${
                                                            isSelected
                                                                ? `rounded-t-lg rounded-b-none border-b-0 -mb-[2px] z-10 pb-4 shadow-sm ${selectedConnect}`
                                                                : `rounded-lg ${unselectedTint}`
                                                        }`}
                                                    >
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="flex items-start gap-2 min-w-0">
                                                            {isAuditSelectableForBulk(approval, project?.requirement, auditCheckpointCounts.get(audit._id), audit._id === selectedAuditId ? existingProjectAssets.length : undefined, audit._id === selectedAuditId ? equipmentCount : undefined) && (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedForBulk.has(audit._id)}
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    onChange={() => toggleBulkSelection(audit._id)}
                                                                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                                    title="Select to send for approval"
                                                                />
                                                            )}
                                                            <span className="text-sm font-semibold leading-snug min-w-0">
                                                                <span className="truncate">{getAuditTypeLabel(audit)}</span>
                                                                <span className="font-normal opacity-70"> — {formatFrequency(audit.auditFrequency)}</span>
                                                            </span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setEditingAudit(audit); }}
                                                                className="shrink-0 -mr-1 -mt-0.5 p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-black/5 transition-colors"
                                                                title="Edit audit"
                                                            >
                                                                <Edit2 size={13} strokeWidth={2} />
                                                            </button>
                                                        </div>
                                                        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                                                            {isSelected && (
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-600 text-white">
                                                                    <CheckCircle2 size={10} strokeWidth={2.5} /> Selected
                                                                </span>
                                                            )}
                                                            {auditUpdated ? (
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">Updated</span>
                                                            ) : isRejected ? (
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-red-100 text-red-700 border border-red-200"><XCircle size={10} /> Rejected</span>
                                                            ) : approved ? (
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-emerald-100 text-emerald-700 border border-emerald-200"><CheckCircle size={10} /> Approved</span>
                                                            ) : submitted ? (
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-600 border border-slate-200"><Clock size={10} /> In review</span>
                                                            ) : (
                                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-medium bg-slate-100 text-slate-500 border border-slate-200">Draft</span>
                                                            )}
                                                            <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                                                                <Users size={10} /> {auditorCount}
                                                            </span>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Connected detail panel for the selected audit — light section colors */}
                                        {selAudit && (
                                            <div className={`rounded-b-xl border-2 border-t-0 -mt-[2px] p-4 ${panelClass}`}>
                                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                                    <div className="min-w-0">
                                                        <h5 className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                                                            {getAuditTypeLabel(selAudit)}
                                                            <span className="font-normal text-slate-500">— {formatFrequency(selAudit.auditFrequency)}</span>
                                                        </h5>
                                                        <div className="mt-1 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                                                            <span className="inline-flex items-center gap-1"><Calendar size={12} className="text-slate-400" /> {formatAuditStartDate((selAudit as any).auditStartDate)}</span>
                                                            {selAudit.scope != null && selAudit.scope !== undefined && <span>Scope: {selAudit.scope}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => { setHistoryAuditId(selAudit._id); setHistoryOpen(true); }}
                                                            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors shrink-0"
                                                        >
                                                            <History size={13} /> View history
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => setEditingAudit(selAudit)}
                                                            className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors shrink-0"
                                                        >
                                                            <Edit2 size={13} /> Edit audit
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Auditors */}
                                                <div className="mt-3">
                                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Auditors</p>
                                                    {selAuditorNames.length === 0 ? (
                                                        <span className="text-xs text-slate-400">No auditors assigned</span>
                                                    ) : (
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            {selAuditorNames.slice(0, 4).map((name, i) => (
                                                                <span key={i} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white border border-slate-200 text-[11px] text-slate-700">
                                                                    <span className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-semibold text-slate-600 shrink-0">{getInitials(name)}</span>
                                                                    <span className="truncate max-w-[120px]">{name}</span>
                                                                </span>
                                                            ))}
                                                            {selAuditorNames.length > 4 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setViewingAuditId(selAudit._id)}
                                                                    className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-100 transition-colors"
                                                                >
                                                                    +{selAuditorNames.length - 4}
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Rejection reasons / updated hint */}
                                                {selRejected && !selUpdated && (selRejSections.length > 0 || selRejCpCount > 0) && (
                                                    <div className="mt-3 rounded-lg border border-red-200 bg-red-50/70 p-2.5">
                                                        <div className="flex items-center gap-1.5 text-red-700">
                                                            <XCircle size={13} className="shrink-0" />
                                                            <span className="text-xs font-semibold">
                                                                Rejected: {selRejSections.join(', ')}{selRejCpCount > 0 ? `${selRejSections.length ? ' · ' : ''}${selRejCpCount} checkpoint(s)` : ''}
                                                            </span>
                                                        </div>
                                                        {selRejComments.length > 0 && (
                                                            <p className="mt-1 text-[11px] text-red-700 italic">"{selRejComments.join('" · "')}"</p>
                                                        )}
                                                    </div>
                                                )}
                                                {selUpdated && (
                                                    <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-medium text-amber-800">
                                                        Updated — resubmit for approval
                                                    </div>
                                                )}

                                                {/* Assets & equipment for the selected audit — covered within its panel */}
                                                <div className="mt-4 pt-4 border-t border-slate-200/70">
                                                    <p className="mb-2 text-[11px] text-slate-400 flex items-center gap-1">
                                                        <Package size={11} /> Assets &amp; checkpoints for this audit
                                                    </p>
                                                    {assetsAndEquipmentCard}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                        </div>
                    </div>
                </>
            )}

            {/* Assets & Equipment standalone — only when there are no audits yet, so the
                requester can add assets/equipment first. Once audits exist, this lives
                inside the selected audit's connected panel above. */}
            {(project?._id || project?.id) && !loadingAudits && existingAudits.length === 0 && assetsAndEquipmentCard}

            {/* Audit Form in Right Drawer */}
            {(project?._id || project?.id) && (
                <RightDrawer
                    open={showAuditForm}
                    onClose={() => setShowAuditForm(false)}
                    title="Create Project Audits"
                    size="xl"
                >
                    <AmsProjectAuditForm
                        amsProjectId={project._id || project.id}
                        projectStartDate={project?.startDate}
                        projectEndDate={project?.endDate || (project as any)?.calculatedEndDate || (project as any)?.endProjectDate}
                        projectAuditors={
                            project?.assignedResources?.auditors?.map((a: any) =>
                                typeof a === 'object' ? (a._id || a.id) : a
                            ) || []
                        }
                        onSuccess={() => {
                            handleAuditSuccess();
                            setShowAuditForm(false);
                        }}
                    />
                </RightDrawer>
            )}

            {/* Toggle Confirmation Modal */}
            <AnimatePresence>
                {isConfirmOpen && auditToToggle && (
                    <div
                        className="fixed inset-0 z-[100000] overflow-y-auto bg-black/20"
                        onClick={() => {
                            setIsConfirmOpen(false);
                            setAuditToToggle(null);
                        }}
                    >
                        <div
                            className="flex min-h-full items-center justify-center p-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.div
                                initial={{
                                    opacity: 0,
                                    scale: 0.95
                                }}
                                animate={{
                                    opacity: 1,
                                    scale: 1
                                }}
                                exit={{
                                    opacity: 0,
                                    scale: 0.95
                                }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                className="relative w-full max-w-lg bg-white rounded-lg border border-gray-200 shadow-xl"
                            >
                                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0 ${auditToToggle.status === 'active' ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"
                                            }`}>
                                            {auditToToggle.status === 'active' ? (
                                                <XCircle size={20} className="text-red-500" />
                                            ) : (
                                                <CheckCircle2 size={20} className="text-green-500" />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                                {auditToToggle.status === 'active' ? "Deactivate Audit" : "Activate Audit"}
                                            </h2>
                                            <p className="text-sm text-gray-600 break-words">
                                                {getAuditTypeLabel(auditToToggle)}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setIsConfirmOpen(false);
                                            setAuditToToggle(null);
                                        }}
                                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 border border-gray-200 transition-all duration-200"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                <div className="p-5">
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 bg-amber-50 rounded-lg flex items-center justify-center border border-amber-200 flex-shrink-0">
                                            <AlertTriangle size={14} className="text-amber-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                                                Are you sure you want to{" "}
                                                <span className="font-medium text-gray-900">
                                                    {auditToToggle.status === 'active' ? "deactivate" : "activate"}
                                                </span>{" "}
                                                <span className="font-medium text-blue-600 break-words">{getAuditTypeLabel(auditToToggle)}</span>?
                                            </p>
                                            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                <p className="text-xs text-gray-600 leading-relaxed">
                                                    {auditToToggle.status === 'active'
                                                        ? "This audit will no longer be active for this project."
                                                        : "This audit will become active for this project."
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                                    <button
                                        onClick={() => {
                                            setIsConfirmOpen(false);
                                            setAuditToToggle(null);
                                        }}
                                        disabled={togglingAuditId === auditToToggle._id}
                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={() => handleToggleAuditStatus(auditToToggle)}
                                        disabled={togglingAuditId === auditToToggle._id}
                                        className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md transition-all duration-200 disabled:opacity-50 ${auditToToggle.status === 'active'
                                            ? "bg-red-500 hover:bg-red-600 focus:ring-red-500"
                                            : "bg-green-500 hover:bg-green-600 focus:ring-green-500"
                                            } focus:outline-none focus:ring-2 focus:ring-offset-2`}
                                    >
                                        {togglingAuditId === auditToToggle._id ? (
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        ) : (
                                            <span>
                                                {auditToToggle.status === 'active' ? "Deactivate" : "Activate"}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                )}
            </AnimatePresence>


            {/* Project Asset Drawer (for managing asset checkpoints) */}
            {openProjectAsset && selectedAssetForCheckpoints && (
                <AmsProjectAssetDrawer
                    open={openProjectAsset}
                    onClose={() => {
                        setOpenProjectAsset(false);
                        setSelectedAssetForCheckpoints(null);
                        // Refresh audits so an asset added here (which stages 'not_required'
                        // checkpoints on already-approved audits) reflects in the per-audit
                        // action buttons. (fetchAudits keeps the current selection.)
                        fetchAudits();
                    }}
                    assetName={selectedAssetForCheckpoints.name || 'Asset'}
                    projectId={project._id || project.id}
                    masterAssetId={selectedAssetForCheckpoints.masterAssetId || ""}
                    projectStartDate={project.startDate}
                    projectRequirement={project.requirement}
                />
            )}

            {/* Audit Edit - Right Drawer (only scope + start date editable) */}
            {editingAudit && (
                <RightDrawer
                    open={!!editingAudit}
                    onClose={() => setEditingAudit(null)}
                    title={`Edit Audit - ${getAuditTypeLabel(editingAudit)}`}
                    size="md"
                >
                    <AmsProjectAuditEditModal
                        open={!!editingAudit}
                        onClose={() => setEditingAudit(null)}
                        audit={editingAudit}
                        approval={auditApprovals.get(editingAudit._id) ?? null}
                        projectStartDate={project?.startDate}
                        projectEndDate={project?.endDate || (project as any)?.calculatedEndDate || (project as any)?.endProjectDate}
                        projectAuditors={
                            project?.assignedResources?.auditors?.map((a: any) =>
                                typeof a === 'object' ? (a._id || a.id) : a
                            ) || []
                        }
                        embedded
                        onSuccess={handleAuditUpdateSuccess}
                    />
                </RightDrawer>
            )}

            {/* Audit History Drawer */}
            {historyAuditId && (
                <AmsAuditHistoryDrawer
                    auditId={historyAuditId}
                    isOpen={historyOpen}
                    onClose={() => setHistoryOpen(false)}
                />
            )}

            {/* Audit Details Drawer */}
            {viewingAuditId && (() => {
                const audit = existingAudits.find(a => a._id === viewingAuditId);
                if (!audit) return null;

                return (
                    <RightDrawer
                        open={!!viewingAuditId}
                        onClose={() => setViewingAuditId(null)}
                        title={`Audit Details - ${getAuditTypeLabel(audit)}`}
                        size="xl"
                    >
                        <div className={`border rounded-lg p-4 hover:shadow-md transition-shadow ${audit.status === 'inactive'
                            ? 'border-gray-200 bg-gray-50/50 opacity-75'
                            : 'border-gray-200 bg-white'
                            }`}>
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h5 className="font-semibold text-gray-900 text-sm mb-1">
                                        {getAuditTypeLabel(audit)}
                                    </h5>
                                    <div className="flex items-center gap-2 mt-2">
                                        {audit.status === 'active' ? (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                                                <CheckCircle2 size={12} />
                                                Active
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                                                <XCircle size={12} />
                                                Inactive
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={(e) => handleToggleClick(e, audit)}
                                        title={`Click to ${audit.status === 'active' ? 'Deactivate' : 'Activate'}`}
                                        className={`relative inline-flex items-center justify-center w-10 h-5 rounded-full border transition-all duration-300 select-none ${audit.status === 'active'
                                            ? "bg-green-50 border-green-200 hover:bg-green-100"
                                            : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                                            }`}
                                    >
                                        <div
                                            className={`w-3 h-3 rounded-full transform transition-all duration-300 ${audit.status === 'active'
                                                ? "translate-x-2 bg-green-500"
                                                : "-translate-x-2 bg-gray-400"
                                                }`}
                                        />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2 mt-3">
                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <Calendar size={14} className="text-gray-400" />
                                    <span className="font-medium">Frequency:</span>
                                    <span>{formatFrequency(audit.auditFrequency)}</span>
                                </div>

                                <div className="flex items-center gap-2 text-xs text-gray-600">
                                    <Calendar size={14} className="text-gray-400" />
                                    <span className="font-medium">Audit start date:</span>
                                    <span>{formatAuditStartDate((audit as any).auditStartDate)}</span>
                                </div>

                                <div className="flex items-start gap-2 text-xs text-gray-600">
                                    <Users size={14} className="text-gray-400 mt-0.5" />
                                    <div className="flex-1">
                                        <span className="font-medium">Auditors:</span>
                                        <div className="mt-1 space-y-1">
                                            {audit.auditors && audit.auditors.length > 0 ? (
                                                audit.auditors.map((auditor: any) => (
                                                    <div key={auditor._id || auditor.id} className="text-gray-700">
                                                        • {auditor.name || auditor.email}
                                                    </div>
                                                ))
                                            ) : (
                                                <span className="text-gray-400">No auditors assigned</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-100">
                                    <Clock size={12} className="text-gray-400" />
                                    <span>Created {formatDate(audit.createdAt)}</span>
                                </div>

                                {/* Approval Status */}
                                {project?.requirement !== 'unknown' && auditApprovals.has(audit._id) && (
                                    <div className="mt-3 pt-3 border-t border-gray-100">
                                        {(() => {
                                            const approval = auditApprovals.get(audit._id)!;
                                            const getApprovalStatusBadge = () => {
                                                switch (approval.overallStatus) {
                                                    case 'draft':
                                                        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">Draft</span>;
                                                    case 'pending_review':
                                                        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full"><Clock size={10} /> Pending Review</span>;
                                                    case 'partially_approved':
                                                        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full"><AlertTriangle size={10} /> Partially Approved</span>;
                                                    case 'approved_to_start':
                                                        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full"><CheckCircle2 size={10} /> Approved to Start</span>;
                                                    case 'rejected':
                                                        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full"><XCircle size={10} /> Rejected</span>;
                                                    default:
                                                        return null;
                                                }
                                            };
                                            return (
                                                <></>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </RightDrawer>
                );
            })()}

            {/* Project and Asset Checkpoints are managed in Project Assets section (tree view) when an audit is selected */}

            {/* Master Asset Selection Drawer */}
            <MasterAssetSelectionDrawer
                open={openMasterDrawer}
                onClose={() => setOpenMasterDrawer(false)}
                availableAssets={availableAssets}
                existingProjectAssets={existingProjectAssets}
                onBulkAdd={async (assets) => {
                    if (!selectedAuditId) {
                        toast.error('Select an audit first');
                        return;
                    }
                    try {
                        setLoading(true);
                        const projectId = project._id || project.id;

                        // Sequential creation to ensure order and avoid race conditions
                        for (const asset of assets) {
                            await amsProjectAssetService.createOrGetAmsProjectAsset(
                                projectId,
                                selectedAuditId,
                                asset.masterAssetId
                            );
                        }

                        toast.success(`Successfully added ${assets.length} assets to this audit`);

                        // Close the drawer after successful bulk add
                        setOpenMasterDrawer(false);

                        // Refresh logic - handleEquipmentUpdate will refresh both equipment and project assets
                        handleEquipmentUpdate();

                    } catch (error) {
                        console.error("Bulk add failed:", error);
                        toast.error("Failed to add some assets");
                    } finally {
                        setLoading(false);
                    }
                }}
                onSelectAsset={(asset) => {
                    // Open the Project Asset Drawer for this master asset
                    handleOpenCheckpoints({
                        masterAssetId: asset.masterAssetId,
                        name: asset.name,
                        assetType: asset.name
                    });
                }}
                onCreateAsset={handleOpenCreateAsset}
            />

            {/* Create New Master Asset — pre-scoped to this project's Project Type */}
            <RightDrawer
                open={showCreateAssetForm}
                onClose={() => setShowCreateAssetForm(false)}
                title="Create New Asset"
                size="lg"
            >
                <AssetForm
                    projectTypes={projectTypesForCreate}
                    defaultProjectTypeId={currentProjectTypeId}
                    onAdd={() => {
                        setShowCreateAssetForm(false);
                        loadAvailableAssets();
                    }}
                    onClose={() => setShowCreateAssetForm(false)}
                />
            </RightDrawer>
        </div>
    );
}
